<template>
  <v-dialog persistent v-model="showDialog" max-width="450">

    <!-- BUTTON WITH TOOLTIP -->
    <template v-slot:activator="{ props }" >
      <v-tooltip :text="`Clone ${descriptiveName} '${item.name}'`" location="top">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
              v-bind="props, tooltipProps"
              class="mx-1"
              size="small"
              rounded
              @click="itemCloneName = item.name + ' - Copy'"
              flat
              height="35px"
              min-width="35px"
              max-width="35px"
              color="green"
          >
            <i class="fas fa-copy" />
          </v-btn>
        </template>
      </v-tooltip>
    </template>

    <!-- MODAL CARD -->
    <v-card>
      <v-card-title><b>Confirm Clone</b></v-card-title>
      <v-divider/>
      <v-card-text>
        You're about to clone Project <b>"{{item.name}}"</b>. Please enter a name:
        <v-text-field
            rounded
            variant="solo-filled"
            prepend-inner-icon="fa fa-clone px-5"
            bg-color="#e1f7e7"
            single-line
            :rules="rules"
            flat
            class="mt-3"
            placeholder="Enter a project name..."
            v-model="itemCloneName"

        />
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red" rounded @click="showDialog = false">Cancel</v-btn>
        <v-btn color="green" class="px-5" :disabled="!rules" rounded @click="cloneItem" variant="flat">
          <v-progress-circular class="mr-3" size="small" v-if="isLoading" indeterminate />
          Clone
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
  import {ref} from "vue";
  import {router} from "@inertiajs/vue3";

  const props = defineProps({

    /**
     * Usually, this prop is referring to a database object. A `Project`, a `KPI`, a `PSI`; stuff like that.
     * In this specific component, it's important that this object has `id` and `name` attributes.
     */
    item: {
      type: Object,
      required: true
    },

    /**
     * Array of objects used in project cloning (and maybe project adding), for duplicate checking.
     * Each item of the array must include the `name` and `id` properties.
     */
    items: {
      type: Array,
      required: true
    },

    /**
     * Used for the purpose of the tooltip. It's usually a literal like 'Project' or 'KPI'.
     */
    descriptiveName: {
      type: String,
      required: true
    },

    /**
     * This is used to send the correct data to the server.
     * <br>
     * **Note:** this prop is only working for the purpose of
     * **project** cloning. When another cloning feature becomes available, make sure to handle the options object
     * transferred into the route. Currently, it only fits for `{project_id: <int>}`
     */
    route: {
      type: Object,
      required: false // TODO: Change this when cloning becomes a feature for another item (except projects).
    },

    /**
     * In the future, this will be used to serve the purpose of parameterizing the request of the clone payload.
     */
    routeOptions: {
      type: Object,
      required: false // TODO: Change this when cloning becomes a feature for another item (except projects).
    },

    /**
     * A route, that the user will be redirected to, whenever the task is successful.
     */
    successFallback: {
      type: String,
      required: false,
    },

    /**
     * Used as a Vuetify rule, for the text field that handles the project-clone name.
     */
    rules: {
      type: Array,
      required: true
    }
  });

  const showDialog = ref(false);
  const itemCloneName = ref('');
  const isLoading = ref(false);

  function cloneItem() {
    if (!itemCloneName.value) return;
    if (props.items.some((item) => item.name === itemCloneName.value)) return;

    isLoading.value = true;

    // TODO: This might need refactoring for future uses.
    router.post(props.route({project_id: props.item.id, clone_name: itemCloneName.value}), null, {
      onSuccess: () => {
        if (props.successFallback)
          router.visit(props.successFallback);

        isLoading.value = false;
      }
    });
  }
</script>
